import { create } from 'zustand';

import { ConvivaEvent } from '@src/common/constant/enum';
import { ISankeyData, ISankeyRequestBody } from '@src/emp-3.0/types';

export interface ISankeyStore {
  sankeyRequestBody: ISankeyRequestBody;
  setSankeyRequestBody: (data: Partial<ISankeyRequestBody>) => void;
  generateSankeyRequestBody: any;
  setGenerateSankeyRequestBody: (data: any) => void;
  showConfigModal: boolean;
  setShowConfigModal: (showConfigModal: boolean) => void;
  sankeyData: ISankeyData;
  setSankeyData: (sankeyData: ISankeyData) => void;
  selectedNodeIds: string[];
  setSelectedNodeIds: (selectedNodeIds: string[]) => void;
  availableNodes: { [key: string]: any }[];
  setAvailableNodes: (availableNodes: { [key: string]: any }[]) => void;
  selectedNodeInstances: any[];
  setSelectedNodeInstances: (selectedNodeInstances: any[]) => void;
  checkedOnly: boolean;
  setCheckedOnly: (checkedOnly: boolean) => void;
}

const defaultSankeyRequestBody: ISankeyRequestBody = {
  landing_events: [{ event_name: '', filters: [] }],
  level: 5,
  interval: '',
  direction: 'forward',
  filters: [[{ field: 'app_id', op: 'eq', value: [] }]],
  event_name_filters: [],
  // 1 or 0. if 0, will add event_name != conviva_network_request. if 1, will add breakdown into configs.
  network_request_enabled: 0,
  event_breakdown: [{ event_name: '', breakdowns: [] }],
  top_k_nodes: 6,
  collapse_repeated_events: 0,
};

const defaultSankeyData = {
  direction: 'forward',
  links: [],
  nodes: [],
} as ISankeyData;

const updateRequestBody = (
  sankeyRequestPayload: ISankeyRequestBody,
  data: Partial<ISankeyRequestBody>,
) => {
  const newRequestBody: ISankeyRequestBody = { ...sankeyRequestPayload, ...data };
  // landing event is nw, enable nw
  if (newRequestBody.landing_events[0].event_name === 'conviva_network_request') {
    newRequestBody.network_request_enabled = 1;
  }
  if (newRequestBody.network_request_enabled === 1) {
    if (!newRequestBody.event_name_filters.includes(ConvivaEvent.conviva_network_request)) {
      newRequestBody.event_name_filters.push(ConvivaEvent.conviva_network_request);
    }
  } else {
    newRequestBody.event_name_filters = newRequestBody.event_name_filters.filter(
      (eventName) => eventName !== ConvivaEvent.conviva_network_request,
    );
    newRequestBody.event_breakdown = newRequestBody.event_breakdown.filter(
      (breakdown) => breakdown.event_name !== ConvivaEvent.conviva_network_request,
    );
  }

  // landing event name to event_name_filters
  if (!newRequestBody.event_name_filters.includes(newRequestBody.landing_events[0].event_name)) {
    newRequestBody.event_name_filters.push(newRequestBody.landing_events[0].event_name);
  }

  // use filter events filter breakdowns
  newRequestBody.event_breakdown = newRequestBody.event_breakdown.filter(
    (b) => b.event_name === '' || newRequestBody.event_name_filters.includes(b.event_name),
  );

  // set default breakdowns
  if (newRequestBody.event_breakdown.length === 0) {
    newRequestBody.event_breakdown.push({
      event_name: '',
      breakdowns: [],
    });
  }

  return newRequestBody;
};

export const useSankeyStore = create<ISankeyStore>((set) => ({
  sankeyRequestBody: defaultSankeyRequestBody,
  setSankeyRequestBody: (data) =>
    set((state) => ({ sankeyRequestBody: updateRequestBody(state.sankeyRequestBody, data) })),
  generateSankeyRequestBody: defaultSankeyRequestBody,
  setGenerateSankeyRequestBody: (data) =>
    set((state) => ({
      generateSankeyRequestBody: updateRequestBody(state.generateSankeyRequestBody, data),
    })),
  showConfigModal: true,
  setShowConfigModal: (showConfigModal) => set(() => ({ showConfigModal })),
  sankeyData: defaultSankeyData,
  setSankeyData: (sankeyData) => set(() => ({ sankeyData })),
  selectedNodeIds: [],
  setSelectedNodeIds: (selectedNodeIds) => set(() => ({ selectedNodeIds })),
  availableNodes: [],
  setAvailableNodes: (availableNodes) => set(() => ({ availableNodes })),
  selectedNodeInstances: [],
  setSelectedNodeInstances: (selectedNodeInstances) => set(() => ({ selectedNodeInstances })),
  checkedOnly: false,
  setCheckedOnly: (checkedOnly) => set(() => ({ checkedOnly })),
}));
